// Experimental, conservative boundary proposals. Energy is evidence, not phonetics.
// Never cross a neighbouring word's recorded interval or shrink a selected word.
export const BOUNDARY_VERSION='quiet-gap-v1';
const MAX_SHIFT=0.12;
const BIN_SECONDS=0.005;

export function analyseAudio(buffer) {
  if(!buffer || !Number.isFinite(buffer.sampleRate) || buffer.sampleRate<=0 || !Number.isFinite(buffer.duration) || buffer.duration<=0 || !Number.isInteger(buffer.numberOfChannels) || buffer.numberOfChannels<1)throw new Error('Valid decoded audio is required for boundary analysis.');
  const step=Math.max(1,Math.round(buffer.sampleRate*BIN_SECONDS));
  const channels=Array.from({length:buffer.numberOfChannels},(_,i)=>buffer.getChannelData(i));
  const length=Math.min(...channels.map(c=>c.length));
  const count=Math.floor(length/step),power=new Float64Array(count);
  for(let bin=0;bin<count;bin++){
    // Maximum channel power protects speech in an isolated/quiet speaker channel.
    for(const channel of channels){let sum=0;for(let n=bin*step;n<(bin+1)*step;n++)sum+=channel[n]**2;
      power[bin]=Math.max(power[bin],sum/step);
    }
  }
  return {power,step:step/buffer.sampleRate,duration:buffer.duration};
}

function edgeProposal(time,low,high,audio) {
  const unchanged=reason=>({original:time,value:time,reason});
  if(high-low<0.025)return unchanged('insufficient-gap');
  const {power,step}=audio;
  const from=Math.max(0,Math.ceil(low/step)),to=Math.min(power.length,Math.floor(high/step));
  const context=Array.from(power.slice(Math.max(0,from-20),Math.min(power.length,to+20))).sort((a,b)=>a-b);
  const reference=context[Math.floor(context.length*0.85)] || 0;
  if(reference<1e-7)return unchanged('no-speech-evidence');
  const threshold=reference*0.025; // Require a sustained, clearly quieter region.
  const edgeFrom=Math.max(0,Math.floor((time-0.015)/step));
  const edgeTo=Math.min(power.length,Math.ceil((time+0.015)/step));
  if(edgeTo>edgeFrom && power.slice(edgeFrom,edgeTo).every(value=>value<=threshold))return unchanged('already-quiet');
  const runs=[];
  let start=null;
  for(let i=from;i<=to;i++){
    if(i<to && power[i]<=threshold){if(start===null)start=i;}
    else if(start!==null){if((i-start)*step>=0.025)runs.push([start*step,i*step]);start=null;}
  }
  if(!runs.length)return unchanged('no-clear-gap');
  // If the existing edge already lies safely in a quiet run, leave it alone.
  if(runs.some(([a,b])=>time>=a+0.005 && time<=b-0.005))return unchanged('already-quiet');
  const candidates=runs.map(([a,b])=>(a+b)/2).sort((a,b)=>Math.abs(a-time)-Math.abs(b-time));
  const value=candidates[0];
  if(Math.abs(value-time)<0.005)return unchanged('already-quiet');
  return {original:time,value,reason:'quiet-gap'};
}

export function refineRange(words,range,audio,duration) {
  const report={version:BOUNDARY_VERSION,start:{original:range.start,value:range.start,reason:'unchanged'},end:{original:range.end,value:range.end,reason:'unchanged'}};
  const unchanged=reason=>({start:range.start,end:range.end,report:{...report,start:{...report.start,reason},end:{...report.end,reason}}});
  if(!audio || Math.abs(audio.duration-duration)>0.15)return unchanged('audio-duration-mismatch');
  if(!Number.isFinite(range.start)||!Number.isFinite(range.end)||range.start<0||range.end>duration||range.end<=range.start)return unchanged('invalid-range');
  const first=words.findIndex(w=>w.start===range.start),last=words.findIndex(w=>w.end===range.end);
  if(first<0 || last<first)return unchanged('manual-boundary');
  if(words.some((w,i)=>!Number.isFinite(w.start)||!Number.isFinite(w.end)||w.start<0||w.end<=w.start||w.end>duration||(i>0&&w.start<words[i-1].end)))return unchanged('ambiguous-word-timing');
  // Treat long pauses as editorial rhythm, not a reason to pad the selection.
  const before=words[first-1],after=words[last+1];
  report.start=before && range.start-before.end>0.25 ? {...report.start,reason:'long-pause'} : edgeProposal(range.start,Math.max(0,range.start-MAX_SHIFT,before?.end ?? 0),range.start,audio);
  report.end=after && after.start-range.end>0.25 ? {...report.end,reason:'long-pause'} : edgeProposal(range.end,range.end,Math.min(duration,range.end+MAX_SHIFT,after?.start ?? duration),audio);
  return {start:report.start.value,end:report.end.value,report};
}
